import React from 'react';
import { useSelect } from 'downshift';
import classNames from 'classnames';
import { useHover } from 'react-use';
import VipWrapper from '@components/VipWrapper';
import IconExpand from '../icons/IconExpand';
import Button from '../Button';
import { IconCheck } from './IconCheck';
import DropdownMenu from './DropdownMenu';
import type { DropdownItem } from './types';

function Dropdown<K, IT extends DropdownItem<K>>({
  items,
  selected = null,
  placeholder = '请选择',
  scheme = 'dark',
  disabled = false,
  warning = false,
  className,
  onChange,
  getTitleName,
}: {
  className?: string;
  selected?: IT['id'] | null;
  placeholder?: string;
  scheme?: 'dark' | 'light';
  disabled?: boolean;
  warning?: boolean;
  items: IT[];
  onChange: (id: IT['id']) => void;
  getTitleName?: (name: string) => string;
}) {
  const {
    isOpen,
    getToggleButtonProps,
    highlightedIndex,
    getMenuProps,
    selectedItem,
    getItemProps,
  } = useSelect({
    items,
    selectedItem: items.find((x) => x.id === selected) ?? null,
    onSelectedItemChange: (state) => {
      if (state.selectedItem !== undefined && state.selectedItem !== null) {
        onChange(state.selectedItem.id);
      }
    },
  });
  const renderItem = (item: IT, index: number) => {
    return (
      <div
        key={item.id}
        className={classNames(
          'flex h-30 cursor-pointer items-center justify-between px-10',
          {
            'bg-white-light': highlightedIndex === index,
          }
        )}
        {...getItemProps({ item, index })}
      >
        <div className="flex truncate">
          <VipWrapper showVip={item.showVip}>{item.name}</VipWrapper>
        </div>
        <div className="ml-20 flex items-center">
          {selectedItem?.id === item.id && <IconCheck />}
        </div>
      </div>
    );
  };

  const getName = () => {
    if (!selectedItem) return placeholder;
    if (getTitleName) return getTitleName(selectedItem.name);
    return selectedItem.name;
  };

  const [btn] = useHover((isHovered) => (
    <Button
      disabled={disabled}
      scheme={scheme === 'light' ? 'white' : 'dark'}
      className={classNames(
        'w-full select-none',
        { 'ring-2 ring-red': warning },
        className
      )}
      {...getToggleButtonProps()}
    >
      <VipWrapper showVip={selectedItem?.showVip}>
        <div className="flex truncate">{getName()}</div>
      </VipWrapper>
      <IconExpand
        scheme={scheme}
        className="ml-auto flex-shrink-0"
        active={isHovered}
      />
    </Button>
  ));

  return (
    <div className="relative">
      {btn}
      <DropdownMenu isOpen={isOpen} {...getMenuProps()}>
        {items.map(renderItem)}
      </DropdownMenu>
    </div>
  );
}

export default Dropdown;
